import React from 'react';
import theme from './DeadlineForm.module.scss';
import { ProjectDeadline } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidationForm, ValidatingTextArea } from '@/components/forms/validation';
import MandatoryIndicator from '@/components/forms/MandatoryIndicator';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';
import { DatePicker } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, Checkbox, ConfirmButton } from '@ygtechnologies/rubus-ui';

interface DeadlineFormProps<T extends ProjectDeadline = ProjectDeadline> {
    value: T;
    onChange(newValue: T): void;
    onSaveClick(value: T): void;
    onRevertClick(): void;
    onCancelClick?(): void;
    isSaving: boolean;
    hasGroupEdits: boolean;
    hasUnitEdits: boolean;
}

const DeadlineForm: React.FC<DeadlineFormProps> = ({
    value,
    onChange,
    onSaveClick,
    onRevertClick,
    onCancelClick,
    isSaving,
    hasGroupEdits,
    hasUnitEdits,
}) => {
    const triggerChange = (changes: Partial<ProjectDeadline>) => {
        const newValue = { ...value, ...changes };
        triggerHandler(onChange, newValue);
    };

    const handleSaveClick = () => {
        triggerHandler(onSaveClick, value);
    };

    const handleRevertAll = () => {
        triggerHandler(onRevertClick);
    };

    return (
        <ValidationForm onSubmit={handleSaveClick} className="form">
            <div className={theme.row}>
                <label className={theme.name}>
                    <Trans>Deadline name</Trans>
                    <MandatoryIndicator />
                    <ValidatingInput
                        onChange={(e) => triggerChange({ name: e.target.value })}
                        value={value.name}
                        required
                        className="form-control"
                        placeholder={t`Deadline name`}
                    />
                </label>

                <label className={theme.date}>
                    <Trans>Deadline date</Trans>
                    <MandatoryIndicator />
                    <div className="form-control-unstyled">
                        <DatePicker
                            onChange={(date) => {
                                if (date) date.setHours(12);
                                triggerChange({ deadline: date! });
                            }}
                            value={parseISODate(value.deadline)}
                            className="form-control"
                        />
                    </div>
                </label>
            </div>
            <div>
                {(hasGroupEdits || hasUnitEdits) && (
                    <AlertBanner type="warning">
                        <Trans>This project deadline contains overrides in:</Trans> &nbsp;
                        {hasGroupEdits && (
                            <strong>
                                <Trans>Unit groups</Trans>
                            </strong>
                        )}
                        {hasUnitEdits && (
                            <>
                                {hasGroupEdits && (
                                    <>
                                        &nbsp;<Trans>and</Trans>&nbsp;
                                    </>
                                )}
                                <strong>
                                    <Trans>Units</Trans>
                                </strong>
                            </>
                        )}
                    </AlertBanner>
                )}
            </div>
            <div>
                <label className={theme.description}>
                    <Trans>Description</Trans>
                    <ValidatingTextArea
                        className="form-control"
                        value={value.description}
                        onChange={(e) => triggerChange({ description: e.target.value })}
                    />
                </label>

                <AlertBanner type="info" className={theme.banner}>
                    <Trans>
                        <strong>Please note</strong> this is the text will appear in options, you can use this text to
                        describe the options within each deadline.
                    </Trans>
                </AlertBanner>
            </div>
            <hr />

            <div className={theme.checkbox}>
                <Checkbox
                    checked={value.isChangeRequestDeadline}
                    onChange={(e) => triggerChange({ isChangeRequestDeadline: e.target.checked })}>
                    <Trans>Use as change request deadline</Trans>
                </Checkbox>
            </div>
            <hr />
            {(hasGroupEdits || hasUnitEdits) && (
                <div className={theme.red}>
                    <Trans>
                        Revert overrides for all unit groups and units for: <strong>{value.name}</strong>
                    </Trans>
                </div>
            )}
            <div className={theme.deadlineButtons}>
                <ButtonBar>
                    <ButtonBar.Right>
                        {onCancelClick && (
                            <Button type="button" variant="tertiary" onClick={onCancelClick}>
                                <Trans>Cancel</Trans>
                            </Button>
                        )}
                        <Button type="submit" variant="primary" loading={isSaving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
                {(hasGroupEdits || hasUnitEdits) && (
                    <div>
                        <ConfirmButton
                            variant="danger"
                            onConfirm={handleRevertAll}
                            dialogTitle={<Trans>Revert deadlines</Trans>}
                            dialogBody={
                                <Trans>Are you sure you want to revert all the deadlines back to project default?</Trans>
                            }
                            confirmButton={{
                                variant: 'danger',
                                children: <Trans>Revert</Trans>,
                            }}>
                            <Trans>Revert to project deadline</Trans>
                        </ConfirmButton>
                    </div>
                )}
            </div>
        </ValidationForm>
    );
};

export default DeadlineForm;
